import "./tagGroupForm.scss";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Formik } from "formik";
import * as Yup from "yup";
import * as tagAction from "../../../../redux/actions/tag.action";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import Select from "react-select";
import { getTheme } from "../../../../helpers/select.theme";
import * as tagGroupAction from "../../../../redux/actions/tagGroup.action";
import {
  getOnlyTagOptions,
  processTagOptions,
  getInitialTagValues,
} from "../../../../helpers/select";
import { useTranslation } from "react-i18next";
import ModalConfirmation from "../../../modalconfirmation/ModalConfirmation";
import { customStyles } from '../../../../helpers/select.custom';

const TagGroupForm = () => {
  const dispatch = useDispatch();
  const { entityId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [customResetForm, setCustomResetForm] = useState(() => {});
  const [renderSuccessAlert, setRenderSuccessAlert] = useState(false);
  const [submittedForm, setSubmittedForm] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [tagOptions, setTagGroupOptions] = useState([]);
  const [showExitModal, setShowExitModal] = useState(false);

  const { tagGroupDetails, tagList, tagGroup, error } = useSelector(
    (state) => ({
      tagGroupDetails: state.tagGroup.tagGroupDetails,
      tagList: state.tag.tagList,
      tagGroup: state.tagGroup.tagGroup,
      error: state.tagGroup.tagGroupError,
    })
  );

  const onSubmitForm = (values) => {
    setDisableSubmit(true); //Disable submit button

    const formData = new FormData();

    const { tags: tagList = [], title } = values;

    const { tags } = processTagOptions(tagList);

    const formBody = {
      tags,
      title,
    };

    formData.append("tagGroup", JSON.stringify(formBody));

    if (entityId) {
      tagGroupAction.update(dispatch, entityId, formData);
    } else {
      tagGroupAction.create(dispatch, formData);
    }

    setSubmittedForm(true); // Flag to make sure that the form was submitted
    setDisableSubmit(false); //Reenable submit button
  };

  useEffect(() => {
    tagAction.fetchSelectOptions(dispatch);

    if (entityId) {
      //If we have an id then load data
      tagGroupAction.fetchDetails(dispatch, entityId);
    }
  }, [dispatch, entityId]);

  useEffect(() => {
    setTagGroupOptions(getOnlyTagOptions(tagList));
  }, [JSON.stringify(tagList)]);

  useEffect(() => {
    //Render success alert and redirect
    if (submittedForm && tagGroup) {
      setRenderSuccessAlert(true);
      if (!entityId) {
        customResetForm();
      }
    }
  }, [entityId, tagGroup, submittedForm, navigate]);

  const initialValues = {
    title: entityId ? tagGroupDetails?.title || "" : "",
    tags: entityId ? getInitialTagValues(tagGroupDetails) : [],
  };

  const formSchema = Yup.object().shape({
    title: Yup.string().required("Required field"),
    tags: Yup.mixed(),
  });

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={formSchema}
      onSubmit={onSubmitForm}
    >
      {(formik) => {
        const {
          handleChange,
          handleSubmit,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          values,
          resetForm,
        } = formik;

        return (
          <div className="tag">
            <div className="tag-container">
              {error && <Alert variant="danger">{error}</Alert>}
              {renderSuccessAlert && (
                <Alert
                  variant="success"
                  onClose={() => {
                    setRenderSuccessAlert(false);
                  }}
                >
                  {entityId ? t("tags.editSuccess") : t("tags.createSuccess")}
                </Alert>
              )}
              <div className="tag-header">
                <h1 className="tag-title">
                  {entityId ? t("tags.editTagGroup") : t("tags.newTagGroup")}
                </h1>
              </div>
              <Form className="tag-form">
                <div className="tag-form-layout">
                  <div className="form-column">
                    <div className="form-row">
                      <div className="form-input">
                        <Form.Label>{t("tags.title")}</Form.Label>
                        <Form.Control
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={errors.title && touched.title}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.title}
                        </Form.Control.Feedback>
                      </div>
                      <div className="form-input">
                        <Form.Label>{t("tags.includetags")}</Form.Label>
                        <Select
                          theme={(theme) => getTheme(theme)}
                          className="form-select-custom"
                          isMulti
                          styles={customStyles}
                          value={values.tags}
                          onChange={(option) => setFieldValue("tags", option)}
                          closeMenuOnSelect={false}
                          onBlur={handleBlur}
                          options={tagOptions}
                          isInvalid={errors.tags && touched.tags}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.tags}
                        </Form.Control.Feedback>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-action-group">
                  <Button
                    onClick={() => {
                      if (formik.dirty && !renderSuccessAlert) {
                        setShowExitModal(true);
                      } else {
                        window.history.back();
                      }
                    }}
                    className="form-action"
                    variant="outline-primary"
                  >
                    {t("buttons.back")}
                  </Button>
                  <Button
                    type="submit"
                    className="form-action form-action-primary"
                    variant="outline-primary"
                    onClick={(e) => {
                      setCustomResetForm(() => resetForm);
                      handleSubmit(e);
                    }}
                    disabled={disableSubmit}
                  >
                    {t("buttons.save")}
                  </Button>
                </div>
              </Form>
            </div>
            <ModalConfirmation
              show={showExitModal}
              handleShow={setShowExitModal}
              title={t("modalconfirmation.titleExit")}
              mainAction={() => setShowExitModal(false)}
              secondaryAction={() => window.history.back()}
              question={t("modalconfirmation.exitquestion")}
              mainButton={t("modalconfirmation.continue")}
              secondaryButton={t("modalconfirmation.yesleave")}
            />
          </div>
        );
      }}
    </Formik>
  );
};

export default TagGroupForm;
